import { useEffect, useRef } from 'react'

import { animateScroll as scroll, Link } from 'react-scroll'
import { ArrowForwardIos as ArrowForwardIosIcon } from '@material-ui/icons'

// Components
import { CardButton } from './components/CardButton'
import { OrLineDivisor } from 'components/GoalWizard/OrLineDivisor'

// Assets
import { DirectCommission } from 'assets/Icons/Stepper/Commission/DirectCommission'
import { CommissionFloor } from 'assets/Icons/Stepper/Commission/CommissionFloor'
import { MultiplePeriods } from 'assets/Icons/Stepper/Period/MultiplePeriods'
import { SinglePeriod } from 'assets/Icons/Stepper/Period/SinglePeriod'
import { MonthlyResult } from 'assets/Icons/Stepper/CommissionBase/MonthlyResult'
import { PeriodResult } from 'assets/Icons/Stepper/CommissionBase/PeriodResult'
import { FixedGoal } from 'assets/Icons/Stepper/GoalDistribution/FixedGoal'
import { DistributedGoal } from 'assets/Icons/Stepper/GoalDistribution/DistributedGoal'
import { StoreTotal } from 'assets/Icons/Stepper/ReceiveCommission/StoreTotal'
import { IndividualGoal } from 'assets/Icons/Stepper/ReceiveCommission/IndividualGoal'

// Styles
import { CardContent, Container } from './style'
import {
  CardContainer,
  CreateIcon,
  EditButton,
  Element,
  NextStepButton,
  Title
} from 'components/GoalWizard/styles'

export interface CommissionStepValues {
  directCommission: boolean | null
  periodType: 'single' | 'multiple' | null
  splitCommission: boolean | null
  distribute: boolean | null
  storeSold: boolean | null
}

interface CommissionStepProps {
  values: CommissionStepValues
  onSelectDirectCommission: (value: boolean) => void
  onSelectPeriodType: (value: 'single' | 'multiple') => void
  onSelectSplitCommission: (value: boolean) => void
  onSelectDistribute: (value: boolean) => void
  onSelectStoreSold: (value: boolean) => void
  showEditButtons?: boolean
  onEditStep?: (stepKeys: string[]) => void
  showNextStepButton?: boolean
  onNextStep?: () => void
}

const CommissionStep = ({
  values,
  onSelectDirectCommission,
  onSelectPeriodType,
  onSelectSplitCommission,
  onSelectDistribute,
  onSelectStoreSold,
  showEditButtons = false,
  onEditStep,
  showNextStepButton = false,
  onNextStep
}: CommissionStepProps) => {
  const {
    directCommission,
    periodType,
    splitCommission,
    distribute,
    storeSold
  } = values

  const isCommissionSelected = directCommission !== null
  const isPeriodSelected = periodType !== null
  const isSplitCommissionSelected = splitCommission !== null
  const isDistributeSelected = distribute !== null
  const isStoreSoldSelected = storeSold !== null

  const hasMultiplePeriods = periodType === 'multiple'

  const areAllCardsSelected =
    isCommissionSelected &&
    isPeriodSelected &&
    isSplitCommissionSelected &&
    isDistributeSelected &&
    isStoreSoldSelected
  const shouldScrollToBottomOnMount = useRef(areAllCardsSelected)

  useEffect(() => {
    shouldScrollToBottomOnMount.current ? scroll.scrollToBottom() : scroll.scrollToTop()
  }, [])

  const handleSelectDirectCommission = (value: boolean) => {
    scroll.scrollToBottom()
    onSelectDirectCommission(value)
  }

  const handleSelectPeriodType = (value: 'single' | 'multiple') => {
    scroll.scrollToBottom()
    onSelectPeriodType(value)
  }

  const handleSelectSplitCommission = (value: boolean) => {
    scroll.scrollToBottom()
    onSelectSplitCommission(value)
  }

  const handleSelectDistribute = (value: boolean) => {
    scroll.scrollToBottom()
    onSelectDistribute(value)
  }

  const handleSelectStoreSold = (value: boolean) => {
    scroll.scrollToBottom()
    onSelectStoreSold(value)
  }

  return (
    <Container>
      <Element name='commission'>
        <CardContainer isSelected={isCommissionSelected}>
          <Title>Como será a remuneração dos vendedores?</Title>
          <CardContent>
            <Link to='period' offset={-100} smooth={true}>
              <CardButton
                id='pureCommission'
                dataCy='pureCommissionButton'
                isSelected={directCommission === true}
                buttonAction={() => handleSelectDirectCommission(true)}
              >
                <DirectCommission />
              </CardButton>
            </Link>

            <OrLineDivisor />

            <Link to='period' offset={-90} smooth={true}>
              <CardButton
                id='salaryPlusCommission'
                dataCy='salaryPlusCommissionButton'
                isSelected={directCommission === false}
                buttonAction={() => handleSelectDirectCommission(false)}
              >
                <CommissionFloor />
              </CardButton>
            </Link>
          </CardContent>
        </CardContainer>
        {showEditButtons && isCommissionSelected && onEditStep && (
          <Link to='commission' offset={-170} smooth={true}>
            <EditButton
              onClick={() => {
                onEditStep(['directCommission'])
              }}
            >
              Alterar
              <CreateIcon />
            </EditButton>
          </Link>
        )}
      </Element>

      {(isCommissionSelected || isPeriodSelected) && (
        <Element name='period'>
          <CardContainer isSelected={isPeriodSelected}>
            <Title>Qual o período da meta?</Title>
            <CardContent>
              <Link to='commission-base' smooth={true} offset={-100}>
                <CardButton
                  id='uniquePeriod'
                  dataCy='uniquePeriodButton'
                  isSelected={periodType === 'single'}
                  buttonAction={() => handleSelectPeriodType('single')}
                >
                  <SinglePeriod />
                </CardButton>
              </Link>

              <OrLineDivisor />

              <Link to='commission-base' smooth={true} offset={-100}>
                <CardButton
                  id='multiplePeriods'
                  dataCy='multiplePeriodsButton'
                  isSelected={periodType === 'multiple'}
                  buttonAction={() => handleSelectPeriodType('multiple')}
                >
                  <MultiplePeriods />
                </CardButton>
              </Link>
            </CardContent>
          </CardContainer>
          {showEditButtons && isPeriodSelected && onEditStep && (
            <Link to='period' offset={-170} smooth={true}>
              <EditButton
                onClick={() => {
                  onEditStep(['sections', 'days', 'splitCommission'])
                }}
              >
                Alterar
                <CreateIcon />
              </EditButton>
            </Link>
          )}
        </Element>
      )}

      {hasMultiplePeriods && (
        <Element name='commission-base'>
          <CardContainer isSelected={isSplitCommissionSelected}>
            <Title>Qual a referência para receber a comissão?</Title>
            <CardContent>
              <Link to='goal-distribution' offset={-100} smooth={true}>
                <CardButton
                  id='monthSales'
                  dataCy='salesByMonthButton'
                  isSelected={splitCommission === false}
                  buttonAction={() => handleSelectSplitCommission(false)}
                >
                  <MonthlyResult />
                </CardButton>
              </Link>

              <OrLineDivisor />

              <Link to='goal-distribution' offset={-100} smooth={true}>
                <CardButton
                  id='salesByPeriod'
                  dataCy='salesByPeriodButton'
                  isSelected={splitCommission === true}
                  buttonAction={() => handleSelectSplitCommission(true)}
                >
                  <PeriodResult />
                </CardButton>
              </Link>
            </CardContent>
          </CardContainer>
          {showEditButtons && isSplitCommissionSelected && onEditStep && (
            <Link to='commission-base' offset={-170} smooth={true}>
              <EditButton
                onClick={() => {
                  onEditStep(['splitCommission'])
                }}
              >
                Alterar
                <CreateIcon />
              </EditButton>
            </Link>
          )}
        </Element>
      )}

      {(isSplitCommissionSelected || isDistributeSelected) && (
        <Element name='goal-distribution'>
          <CardContainer isSelected={isDistributeSelected}>
            <Title>A meta poderá ser redistribuída para a equipe?</Title>
            <CardContent>
              <Link to='receive-commission' offset={-100} smooth={true}>
                <CardButton
                  id='doNotRedistributeGoal'
                  dataCy='doNotRedistributeGoalButton'
                  isSelected={distribute === false}
                  buttonAction={() => handleSelectDistribute(false)}
                >
                  <FixedGoal />
                </CardButton>
              </Link>

              <OrLineDivisor />

              <Link to='receive-commission' offset={-100} smooth={true}>
                <CardButton
                  id='redistributeGoal'
                  dataCy='redistributeGoalButton'
                  isSelected={distribute === true}
                  buttonAction={() => handleSelectDistribute(true)}
                >
                  <DistributedGoal />
                </CardButton>
              </Link>
            </CardContent>
          </CardContainer>
          {showEditButtons && isDistributeSelected && onEditStep && (
            <Link to='goal-distribution' offset={-170} smooth={true}>
              <EditButton
                onClick={() => {
                  onEditStep(['distribute'])
                }}
              >
                Alterar
                <CreateIcon />
              </EditButton>
            </Link>
          )}
        </Element>
      )}

      {(isDistributeSelected || isStoreSoldSelected) && (
        <Element name='receive-commission'>
          <CardContainer isSelected={isStoreSoldSelected}>
            <Title>Qual resultado garante a comissão do vendedor?</Title>
            <CardContent>
              <Link to='next-step' offset={-170} smooth={true}>
                <CardButton
                  id='storeGoal'
                  dataCy='storeGoalButton'
                  isSelected={storeSold === true}
                  buttonAction={() => handleSelectStoreSold(true)}
                >
                  <StoreTotal />
                </CardButton>
              </Link>

              <OrLineDivisor />

              <Link to='next-step' offset={-170} smooth={true}>
                <CardButton
                  id='sellerGoal'
                  dataCy='individualSellerGoalButton'
                  isSelected={storeSold === false}
                  buttonAction={() => handleSelectStoreSold(false)}
                >
                  <IndividualGoal />
                </CardButton>
              </Link>
            </CardContent>
          </CardContainer>
          {showEditButtons && isStoreSoldSelected && onEditStep && (
            <Link to='receive-commission' offset={-170} smooth={true}>
              <EditButton
                onClick={() => {
                  onEditStep(['storeSold'])
                }}
              >
                Alterar
                <CreateIcon />
              </EditButton>
            </Link>
          )}
        </Element>
      )}

      {showNextStepButton && areAllCardsSelected && onNextStep && (
        <Element name='next-step'>
          <NextStepButton onClick={onNextStep} data-cy='nextStepButton'>
            Próxima etapa
            <ArrowForwardIosIcon />
          </NextStepButton>
        </Element>
      )}
    </Container>
  )
}

export default CommissionStep
